import { useState } from "react"
import styled from "styled-components"

import { ButtonStyleReset, Checkbox } from "@minuthq/meatball-ui-react"
import { colors } from "@minuthq/meatball-ui-react"
import { Text } from "@minuthq/meatball-ui-react"
import { subMonths } from "date-fns"

import { EmptyState } from "src/components/EmptyState"
import { ParadiseDeviceReadingsGraph } from "src/components/Paradise/ParadiseDevices/ParadiseDeviceReadings/ParadiseDeviceReadingsGraph"
import { ParadiseQuickLinks } from "src/components/Paradise/ParadiseDevices/ParadiseDeviceReadings/ParadiseQuickInsights/ParadiseQuickLinks"
import { useParadiseDeviceReadingsQuickLinks } from "src/components/Paradise/ParadiseDevices/ParadiseDeviceReadings/ParadiseQuickInsights/useParadiseDeviceReadingsQuickLinks"
import { ParadiseSelectDeviceReading } from "src/components/Paradise/ParadiseDevices/ParadiseDeviceReadings/ParadiseSelectDeviceReading"
import { ParadiseLayout } from "src/components/Paradise/sharedStyles"
import { isValidDeviceReadingtype } from "src/data/devices/logic/deviceLogic"
import { useFetchParadiseDevice } from "src/data/paradise/paradiseDevices/queries/paradiseDeviceQueries"
import { useFetchParadiseHome } from "src/data/paradise/paradiseHomes/queries/paradiseHomeQueries"
import { useSearchParams } from "src/router/useSearchParams"
import { DateRangePicker } from "src/ui/DateRangePicker/DateRangePicker"
import { MSkeleton } from "src/ui/MSkeleton/MSkeleton"
import { spacing } from "src/ui/spacing"
import { dateToUTCISOStringWithoutTzConversion } from "src/utils/l10n"
import { localStorageFactory } from "src/utils/storageUtil"

const BOX_ZOOM_STORAGE_KEY = "minut.paradise.boxZoom"
const localStorageBoxZoom = localStorageFactory<boolean>({
  key: BOX_ZOOM_STORAGE_KEY,
})

export const PARADISE_QUICK_INSIGHTS_TYPE_PARAM_KEY = "type"

export function ParadiseQuickInsights({ deviceId }: { deviceId: string }) {
  const [boxZoom, setBoxZoom] = useState(localStorageBoxZoom.get() ?? true)

  const { searchParams, setSearchParams } = useSearchParams({
    keys: [
      {
        key: PARADISE_QUICK_INSIGHTS_TYPE_PARAM_KEY,
        type: "string",
      },
    ],
  })

  const deviceReadingType = searchParams.type

  const { quickLinks, toggleQuickLink, hasQuickLink } =
    useParadiseDeviceReadingsQuickLinks()

  const [searchedDeviceReading, setSearchedDeviceReading] = useState(
    deviceReadingType ?? ""
  )
  const [inputDeviceReading, setInputDeviceReading] = useState(
    deviceReadingType ?? ""
  )

  const [selectedDate, setSelectedDate] = useState({
    startAt: subMonths(new Date(), 1),
    endAt: new Date(),
  })

  const [useHomeTimezone, setUseHomeTimezone] = useState(false)

  const fetchParadiseDevice = useFetchParadiseDevice({
    deviceId,
  })
  const device = fetchParadiseDevice.data

  const fetchHome = useFetchParadiseHome({
    homeId: device?.home ?? "",
    options: {
      enabled: !!device?.home,
    },
  })

  if (fetchParadiseDevice.isLoading || !device) {
    return (
      <ParadiseLayout>
        <MSkeleton width="30ch" />
        <MSkeleton width="30ch" />
        <MSkeleton width="30ch" />
      </ParadiseLayout>
    )
  }

  return (
    <ParadiseLayout>
      <SelectWrapper>
        <DeviceReadingsWrapper>
          <ParadiseQuickLinks
            currentReadingType={deviceReadingType}
            quickLinks={quickLinks}
            onClick={(quickLink) => {
              if (quickLink) {
                setSearchParams("type", quickLink)

                setSearchedDeviceReading(quickLink)
                setInputDeviceReading(quickLink)
              }
            }}
          />
          <SelectDeviceReadingWrapper>
            <ParadiseSelectDeviceReading
              device={device}
              selectedDeviceReading={deviceReadingType || ""}
              onChange={(value) => {
                if (value) {
                  setSearchParams("type", value)
                }
              }}
              searchedValue={searchedDeviceReading}
              input={inputDeviceReading}
              onInput={(value) => {
                setInputDeviceReading(value)
                setSearchedDeviceReading(value)
              }}
            />
            <QuickLinkButton
              onClick={() => {
                if (
                  deviceReadingType &&
                  isValidDeviceReadingtype(deviceReadingType)
                ) {
                  toggleQuickLink(deviceReadingType)
                }
              }}
              disabled={
                !deviceReadingType ||
                !isValidDeviceReadingtype(deviceReadingType)
              }
            >
              <Text variant="subtitleS" color="unset">
                {!!deviceReadingType &&
                isValidDeviceReadingtype(deviceReadingType) &&
                hasQuickLink(deviceReadingType)
                  ? "Remove from quick links"
                  : "Add to quick links"}
              </Text>
            </QuickLinkButton>
          </SelectDeviceReadingWrapper>
        </DeviceReadingsWrapper>
        <div>
          <DateRangeBox>
            <DateRangePicker
              key={JSON.stringify(selectedDate)}
              startDate={selectedDate.startAt}
              endDate={selectedDate.endAt}
              onDateRangeChange={({ startDate, endDate }) => {
                setSelectedDate({
                  startAt: new Date(
                    dateToUTCISOStringWithoutTzConversion(startDate)
                  ),
                  endAt: new Date(
                    dateToUTCISOStringWithoutTzConversion(endDate)
                  ),
                })
              }}
              dateRangeProps={{
                minBookingDays: 2,
              }}
              defaultPresetKey="DAY"
            />
            <TimezoneToggle>
              <Checkbox
                checked={useHomeTimezone}
                onCheck={(checked) => setUseHomeTimezone(checked)}
              />
              <Text>
                Use home timezone ({fetchHome.data?.timezone || "UTC"})
              </Text>
            </TimezoneToggle>
            <ZoomStyleToggle>
              <Checkbox
                checked={boxZoom}
                onCheck={(checked) => {
                  setBoxZoom(checked)
                  localStorageBoxZoom.set(checked)
                }}
              />
              <Text>Use box zoom</Text>
            </ZoomStyleToggle>
          </DateRangeBox>
        </div>

        {deviceReadingType && isValidDeviceReadingtype(deviceReadingType) ? (
          <ParadiseDeviceReadingsGraph
            type={deviceReadingType}
            dateRange={selectedDate}
            setDateRange={setSelectedDate}
            device={device}
            useHomeTimezone={useHomeTimezone}
            homeTimezone={fetchHome.data?.timezone ?? "UTC"}
            zoom={boxZoom ? "xy" : "x"}
          />
        ) : (
          <EmptyState title="Please select reading type" icon={<></>} />
        )}
      </SelectWrapper>
    </ParadiseLayout>
  )
}

const SelectWrapper = styled.div`
  display: grid;
  gap: ${spacing.XL2};
`

const DeviceReadingsWrapper = styled.div`
  display: grid;
  gap: ${spacing.M};
`

const SelectDeviceReadingWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr minmax(auto, 200px);
  gap: ${spacing.S};
`

const DateRangeBox = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.M};
`

const TimezoneToggle = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.XS};
`

const ZoomStyleToggle = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.XS};
`

const QuickLinkButton = styled.button`
  ${ButtonStyleReset}
  background-color: ${colors.neutral};
  border-radius: 8px;
  border: none;
  padding: ${spacing.XS2} ${spacing.S};
  transition: background-color 0.1s;

  &:hover:not(:disabled) {
    background-color: ${colors.neutralDark};
  }

  &:disabled {
    color: ${colors.neutralDark3};
    cursor: not-allowed;
  }
`
